/* eslint-disable object-shorthand */

// react imports
import { useState, useEffect } from "react";

// react router imports
import { useNavigate } from "react-router-dom";

// @mui material components
import { Grid, Card, NativeSelect, Icon, Divider } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import TimelineItem from "examples/Timeline/TimelineItem";

// notistack imports
import { useSnackbar } from "notistack";

// uuid import
import uuid from "react-uuid";

// Material Dashboard 2 React context
import { useMaterialUIController, setDisplayName } from "context";

// Firebase imports
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../Firebase";

function Update() {
  const [controller, dispatch] = useMaterialUIController();
  const { update, displayName, currentStudy, statusPageEmpty } = controller;

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState("default");
  const [comment, setComment] = useState("");

  const [shipping, setShipping] = useState(false);
  const [shippingCo, setShippingCo] = useState("fedex");
  const [trackingNumb, setTrackingNumb] = useState("");
  const [trackingLink, setTrackingLink] = useState("");
  const [trackingName, setTrackingName] = useState("");

  const shippingCoLinks = {
    fedex: "https://www.fedex.com/fedextrack/",
    ups: "https://www.ups.com/track",
    usps: "https://tools.usps.com/go/TrackConfirmAction_input",
    dhl: "https://www.dhl.com/tracking.html",
  };

  const newDate = new Date();
  const stringDate = newDate.toLocaleDateString("en-us", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  useEffect(() => {
    setTrackingLink(shippingCoLinks[shippingCo]);
    setTrackingNumb(trackingNumb);
    setTrackingName(
      Object.keys(shippingCoLinks)
        .find((key) => shippingCoLinks[key] === shippingCoLinks[shippingCo])
        .toUpperCase()
    );
  }, [shippingCo]);

  useEffect(() => {
    if (shipping) {
      setComment(
        `This order has shipped! Visit the link below and use your tracking number to track your package.`
      );
      setTrackingLink(shippingCoLinks[shippingCo]);
      setTrackingName(
        Object.keys(shippingCoLinks)
          .find((key) => shippingCoLinks[key] === shippingCoLinks[shippingCo])
          .toUpperCase()
      );
    }
    if (!shipping) {
      setComment("");
      setTrackingLink("");
      setTrackingName("");
      setTrackingNumb("");
    }
  }, [shipping]);

  useEffect(() => {
    if (status === "shipped") {
      setShipping(true);
    } else {
      setShipping(false);
    }
  }, [status]);

  useEffect(() => {
    const getUserDetails = async () => {
      const localStorageName = window.localStorage.getItem("userName");

      if (localStorageName === undefined || localStorageName === null) {
        const authUser = getAuth().currentUser;
        const userDoc = doc(db, "USERS", authUser.email);
        const userDetails = await getDoc(userDoc);
        window.localStorage.setItem("userName", userDetails.data().firstName);
      }

      setDisplayName(dispatch, localStorageName);
    };
    getUserDetails();
  }, [displayName]);

  const contactedOptions = {
    default: "Please choose a status",
    new: "new order",
    underReviewQD: "under review",
    underReviewSponsor: "under review",
    approved: "approved",
    inProduction: "in production",
    shipped: "shipped",
    cancelled: "cancelled",
    edited: "edited",
  };

  const contactedOptionDetails = {
    new: `${update.orderId} is a new order that needs to be approved.`,
    underReviewQD: `${update.orderId} is under review by QD Solutions. Status changed by ${displayName}.`,
    underReviewSponsor: `${update.orderId} is under review by ${
      statusPageEmpty ? "N/A" : update.storeData.sponsor
    }. Status changed by ${displayName}.`,
    approved: `${update.orderId} has been approved. Status changed by ${displayName}.`,
    inProduction: `${update.orderId} is in production.  Status changed by ${displayName}.`,
    shipped: `${update.orderId} has been shipped. Status changed by ${displayName}.`,
    cancelled: `${update.orderId} has been cancelled by ${displayName}.`,
    edited: `${update.orderId} was edited by ${displayName}.`,
  };

  const contactedOptionColors = {
    default: "info",
    new: "warning",
    underReviewQD: "primary",
    underReviewSponsor: "primary",
    inProduction: "info",
    approved: "warning",
    shipped: "success",
    cancelled: "error",
    edited: "info",
  };

  const contactedOptionIcons = {
    default: "add",
    new: "fiber_new",
    underReviewQD: "search",
    underReviewSponsor: "search",
    inProduction: "inventory",
    approved: "check_box",
    shipped: "local_shipping",
    cancelled: "clear",
    edited: "edit",
  };

  const saveStatus = async () => {
    const date = new Date();
    const orderDocRef = doc(db, `${currentStudy}_ORDERS`, update.id);

    const isLastBoolean = contactedOptions[status] === "shipped";

    const timelineItem = {
      id: uuid().substring(0, 10),
      timeStamp: date,
      status: contactedOptions[status],
      statusTitle: contactedOptions[status],
      statusDetails: contactedOptionDetails[status],
      statusColor: contactedOptionColors[status],
      statusIcon: contactedOptionIcons[status],
      comment:
        comment.length === 0
          ? { title: "", details: "" }
          : { title: `Comment from ${displayName}:`, details: comment },
      tracking:
        trackingLink.length === 0
          ? { link: "", numb: "", name: "" }
          : { name: trackingName, link: trackingLink, numb: trackingNumb },
      isLast: isLastBoolean,
    };

    if (status === "shipped") {
      const sendmailApi =
        "https://q1bgx99bv9.execute-api.us-east-1.amazonaws.com/default/storefront_sendmail_orderflow";

      const body = JSON.stringify({
        senderName: displayName,
        senderEmail: update.orderFor,
        senderOrderNumb: update.orderId,
        senderStudy: update.storeData.title,
        senderRoute: "order-shipped",
        senderTrackingNumb: trackingNumb,
        senderTrackingLink: shippingCoLinks[shippingCo],
      });

      console.log(body);

      const requestOptions = {
        method: "post",
        body,
      };

      fetch(sendmailApi, requestOptions)
        .then((response) => {
          if (!response.ok) throw new Error("Error in fetch");
          return response.json();
        })
        .then((response) => {
          console.log(response);
          console.log("Sent to Lambda successfully!");
        })
        .catch((error) => {
          console.log(error);
          console.log("Email didn't work!");
        });
    }

    const variant = "success";
    enqueueSnackbar(`${update.orderId}'s status has been updated!`, {
      variant,
      autoHideDuration: 2000,
    });

    await updateDoc(orderDocRef, {
      lastModified: date,
      status: contactedOptions[status],
      statusDetails: contactedOptionDetails[status],
      statusColor: contactedOptionColors[status],
      statusTimeline: arrayUnion(timelineItem),
    });

    navigate(`/`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sm={6}>
            <Card>
              {statusPageEmpty ? (
                <>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      No Order Chosen
                    </MDTypography>
                  </MDBox>
                  <Card sx={{ height: "100%", boxShadow: "none" }}>
                    <MDBox pb={2} px={2}>
                      <MDBox py={2}>
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            navigate("/orders");
                          }}
                        >
                          <Icon>arrow_back_ios</Icon>
                        </MDButton>
                      </MDBox>
                      <MDTypography variant="h6">
                        You haven&apos;t chosen an order to update.
                      </MDTypography>
                      <MDTypography variant="body2" sx={{ fontSize: ".75rem" }}>
                        Return to the orders page.
                      </MDTypography>
                      <Divider />
                    </MDBox>
                  </Card>
                </>
              ) : (
                <>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      Status Update
                    </MDTypography>
                  </MDBox>
                  <MDBox p={2}>
                    <MDBox py={2}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          navigate(`/${currentStudy}`);
                        }}
                      >
                        <Icon>arrow_back_ios</Icon>
                      </MDButton>
                    </MDBox>
                    <MDTypography variant="body1" color="dark">
                      Order #: {update.orderId}
                    </MDTypography>
                    <MDBox py={2}>
                      <MDTypography variant="body2" color="dark" sx={{ display: "inline" }}>
                        Select Status:{" "}
                      </MDTypography>
                      <NativeSelect
                        onChange={(e) => {
                          setStatus(e.target.value);
                        }}
                        defaultValue="none"
                        inputProps={{
                          name: "status select",
                          id: "uncontrolled-native",
                        }}
                        sx={{ width: "100%" }}
                      >
                        <option value="new">New Order</option>
                        <option value="underReviewQD">Under Review by QD</option>
                        <option value="underReviewSponsor">Under Review by Sponsor</option>
                        <option value="approved">Approved</option>
                        <option value="inProduction">In Production</option>
                        <option value="shipped">Shipped</option>
                        <option value="cancelled">Cancelled</option>
                      </NativeSelect>
                    </MDBox>
                    {status !== "New Order" && (
                      <MDBox pt={2} pb={shipping ? 1 : 3}>
                        <MDInput
                          type="text"
                          multiline
                          rows={3}
                          label="Comment"
                          value={comment}
                          onChange={(e) => {
                            setComment(e.target.value);
                          }}
                          style={{ width: "100%" }}
                        />
                      </MDBox>
                    )}
                    {shipping && (
                      <MDBox pt={2} pb={3}>
                        <MDTypography variant="body2" color="dark" sx={{ display: "inline" }}>
                          Choose Carrier:{" "}
                        </MDTypography>
                        <NativeSelect
                          onChange={(e) => {
                            setShippingCo(e.target.value);
                          }}
                          defaultValue="none"
                          inputProps={{
                            name: "shipping company select",
                            id: "uncontrolled-native",
                          }}
                          sx={{ width: "100%" }}
                        >
                          <option value="fedex">Fedex</option>
                          <option value="ups">UPS</option>
                          <option value="usps">USPS</option>
                          <option value="dhl">DHL</option>
                        </NativeSelect>
                        <MDInput
                          type="text"
                          label="Tracking Number"
                          value={trackingNumb}
                          onChange={(e) => {
                            setTrackingNumb(e.target.value);
                          }}
                          style={{ width: "100%", marginTop: "1rem" }}
                        />
                      </MDBox>
                    )}
                    <MDBox borderRadius={5} bgColor="light" p={2}>
                      <TimelineItem
                        color={contactedOptionColors[status]}
                        icon={contactedOptionIcons[status]}
                        title={contactedOptions[status]}
                        dateTime={stringDate}
                        description={contactedOptionDetails[status]}
                        commentTitle={`Comment from ${displayName}:`}
                        commentDetails={comment}
                        shippingTrackName={trackingName}
                        shippingTrackLink={trackingLink}
                        shippingTrackNumb={trackingNumb}
                        lastItem
                      />
                    </MDBox>
                    <MDBox py={2}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={saveStatus}
                      >
                        Save Status
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Update;
